import * as React from "react";
import { useState, useEffect } from "react";
import { useErrorStore } from "../store/useErrorStore";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import FormHelperText from "@mui/joy/FormHelperText";
import Input from "@mui/joy/Input";
import Stack from "@mui/joy/Stack";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import states from "../data/states.json";
import cities from "../data/state_cities.json";
import Typography from "@mui/joy/Typography";
import Card from "@mui/joy/Card";
import Grid from "@mui/joy/Grid";
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import Autocomplete from "@mui/joy/Autocomplete";
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import PageBreadcrumbs from "../components/common/PageBreadcrumbs";
import CircularProgress from '@mui/joy/CircularProgress';
import Sidebar from "../components/common/Sidebar";
import Header from "../components/common/Header";
import JoyDatePicker from "../components/common/JoyDatePicker";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function EditDonor() {
   const [isLoading, setIsLoading] = useState(true);

   const [donorData, setDonorData] = useState({
      id: "",
      name: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      country: "India",
      pincode: "",
      pan: "",
      email: "",
      birthdate: "",
      donorType: "",
      createdBy: "",
      updatedBy: "",
      createdAt: "",
      updatedAt: "",
   });

   const [errors, setErrors] = useState({
      name: "",
      phone: "",
      email: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      pan: "",
      birthdate: "",
   });

   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isEditing, setIsEditing] = useState(false);
   const [isFormEditable, setIsFormEditable] = useState(true);
   const [message, setMessage] = useState<string | null>(null);
   const { error, setError } = useErrorStore();

   const [searchParams] = useSearchParams();
   const donorIdFromUrl = searchParams.get("id");

   useEffect(() => {
      const fetchDonorById = async (id: string) => {
         setIsLoading(true);
         try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donor/${id}`, {
               method: "GET",
               headers: { "Content-Type": "application/json" },
               credentials: "include",
            });

            if (!response.ok) {
               if (response.status === 401) {
                  const redirectUrl = encodeURIComponent(window.location.href);
                  window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
                  return;
               }
               throw new Error("Failed to fetch donor details.");
            }

            const data = await response.json();
            // Check for default birthdate and set to empty string if needed
            if (data.birthdate === "0001-01-01T00:00:00Z") {
               data.birthdate = "";
            }
            setDonorData(data);
            setOriginalDonorData(data);
            setShowDonorForm(true);
            setIsFormEditable(false);
            setIsEditing(false);
         } catch (error) {
            console.error("Error fetching donor details:", error);
            setError("Failed to load donor details.");
         } finally {
            setIsLoading(false);
         }
      };

      if (donorIdFromUrl) {
         fetchDonorById(donorIdFromUrl);
      }
   }, [donorIdFromUrl]);

   useEffect(() => {
      if (message || error) {
         const timer = setTimeout(() => {
            setMessage(null);
            setError(null);
         }, 3000);
         return () => clearTimeout(timer);
      }
   }, [message, error]);
   const [originalDonorData, setOriginalDonorData] = useState(donorData);
   const [showDonorForm, setShowDonorForm] = useState(false);
   const hasErrors = Object.values(errors).some((error) => error !== "") || !donorData.name || !donorData.phone;

   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      let errorMsg = "";
      let validatedValue = value;

      switch (name) {
         case "name":
            if (!/^[A-Za-z0-9 ]*$/.test(value) || value.length > 24) {
               errorMsg = "Max 24 characters. Only letters, numbers, and spaces allowed.";
            }
            validatedValue = value.slice(0, 24);
            break;

         case "phone":
            if (!/^[1-9][0-9]{9}$/.test(value)) {
               errorMsg = "Must be exactly 10 digits and cannot start with 0.";
            }
            validatedValue = value.slice(0, 10);
            break;

         case "email":
            if (value && !/^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/.test(value)) {
               errorMsg = "Enter a valid email address.";
            }
            break;

         case "birthdate":
            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
               errorMsg = "Birthdate must be in YYYY-MM-DD format.";
            }
            break;

         case "address":
            if (!/^[a-zA-Z0-9 ,\-\/]*$/.test(value) || value.length > 100) {
               errorMsg = "Max 100 characters. Only letters, numbers, ',', '-', and '/' allowed.";
            }
            validatedValue = value.slice(0, 100);
            break;

         case "city":
            validatedValue = value.toUpperCase();
            if (!/^[A-Z ]*$/.test(validatedValue) || validatedValue.length > 20) {
               errorMsg = "Max 20 characters. Only uppercase letters and spaces allowed.";
            }
            validatedValue = validatedValue.slice(0, 20);
            break;

         case "pincode":
            if (!/^[0-9]{0,6}$/.test(value)) {
               errorMsg = "Must be exactly 6 digits.";
            }
            validatedValue = value.slice(0, 6);
            break;

         case "pan":
            validatedValue = value.toUpperCase();
            if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(validatedValue)) {
               errorMsg = "PAN must be exactly 10 characters: 5 letters, 4 digits, 1 letter.";
            }
            validatedValue = validatedValue.slice(0, 10);
            break;

         default:
            break;
      }

      setErrors((prev) => ({ ...prev, [name]: errorMsg }));
      setDonorData((prev) => ({ ...prev, [name]: validatedValue }));
   };

   const handleSelectChange = (event: any, newValue: string | null, field: string) => {
      setDonorData((prev) => ({ ...prev, [field]: newValue || "" }));
   };

   const handleUpdateDonor = async () => {
      setIsSubmitting(true);
      setMessage(null);
      setError(null);

      const formattedBirthdate = donorData.birthdate
         ? dayjs(donorData.birthdate).tz("Asia/Kolkata").format("YYYY-MM-DDTHH:mm:ssZ")
         : null;
      const donorPayloadToSend = {
         ...donorData,
         birthdate: formattedBirthdate,
      };

      try {
         const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donor/${donorData.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(donorPayloadToSend),
            credentials: "include",
         });

         if (!response.ok) {
            if (response.status === 401) {
               const redirectUrl = encodeURIComponent(window.location.href);
               window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
               return;
            }
            throw new Error("Failed to update donor.");
         }

         setMessage("Donor updated successfully!");
         setOriginalDonorData(donorData); // Update original data
         setIsFormEditable(false); // Make form fields read-only
         setIsEditing(false); // Exit edit mode
      } catch (err) {
         setError("Error updating donor. Please try again.");
      } finally {
         setIsSubmitting(false);
      }
   };


   return (
      <Box sx={{ display: "flex", minHeight: "100dvh" }}>
         <Header />
         <Sidebar />
         <Box
            component="main"
            className="MainContent"
            sx={{
               pt: { xs: "calc(12px + var(--Header-height))", md: 3 },
               pb: { xs: 2, sm: 2, md: 3 },
               flex: 1,
               display: "flex",
               flexDirection: "column",
               minWidth: 0,
               gap: 1,
               overflow: "auto",
               bgcolor: "background.body",
            }}
         >
            <Box sx={{ flex: 1, width: "100%" }}>
               <Box
                  sx={{
                     position: "sticky",
                     top: { sm: -100, md: -110 },
                     bgcolor: "background.body",
                     zIndex: 9995,
                  }}
               >
                  <Box sx={{ px: { xs: 2, md: 6 } }}>
                     <PageBreadcrumbs
                        links={[
                           { label: "Donation", href: "/donation" },
                           { label: "Donor", href: "/donation/donor" },
                        ]}
                        current={`ID - ${donorIdFromUrl}`}
                     />
                     <Typography level="h2" component="h1" sx={{ mt: 1, mb: 2 }}>
                        Donor Profile
                     </Typography>
                  </Box>
               </Box>
               <Stack
                  spacing={4}
                  sx={{
                     display: "flex",
                     maxWidth: "800px",
                     mx: "auto",
                     px: { xs: 2, md: 6 },
                     py: { xs: 2, md: 3 },
                  }}
               >
                  <Card>
                     <Box sx={{ mb: 1 }}>
                        <Typography level="title-md">Donor Details</Typography>
                        <Typography level="body-sm">
                           View and edit donor details here.
                        </Typography>
                     </Box>
                     {/* Doner Profile */}
                     {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                           <CircularProgress />
                        </Box>
                     ) : (showDonorForm || donorData.id) ? (
                        <Grid container spacing={2} sx={{ flexGrow: 1, my: 1 }}>
                           <Grid xs={12} sm={4}>
                              <FormControl sx={{ flexGrow: 2 }} error={!!errors.name}>
                                 <FormLabel >Name</FormLabel>
                                 <Input
                                    size="sm"
                                    placeholder="Full name"
                                    name="name"
                                    value={donorData.name}
                                    onChange={handleInputChange}
                                    readOnly={!isFormEditable}
                                 />
                                 {errors.name && <FormHelperText>{errors.name}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl sx={{ flexGrow: 1 }} error={!!errors.phone}>
                                 <FormLabel>Phone</FormLabel>
                                 <Input
                                    size="sm"
                                    placeholder="e.g., 1234567890"
                                    name="phone"
                                    value={donorData.phone}
                                    onChange={handleInputChange}
                                    readOnly={!isFormEditable}
                                 />
                                 {errors.phone && <FormHelperText>{errors.phone}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl sx={{ flexGrow: 1 }} error={!!errors.email}>
                                 <FormLabel>Email</FormLabel>
                                 <Input
                                    size="sm"
                                    type="email"
                                    placeholder="username@example.com"
                                    name="email"
                                    value={donorData.email}
                                    onChange={handleInputChange}
                                    readOnly={!isFormEditable}
                                 />
                                 {errors.email && <FormHelperText>{errors.email}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl>
                                 <FormLabel>Birthdate</FormLabel>
                                 <JoyDatePicker
                                    name="birthdate"
                                    value={donorData.birthdate ? dayjs(donorData.birthdate) : null}
                                    onChange={(newValue) => {
                                       const formattedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
                                       setDonorData((prev) => ({ ...prev, birthdate: formattedDate }));
                                       setErrors((prev) => ({ ...prev, birthdate: formattedDate ? "" : "Date is required." }));
                                    }}
                                    format="YYYY-MM-DD"
                                    slotProps={{
                                       textField: {
                                          size: "small",
                                          sx: { height: "32px", minHeight: "32px", fontSize: "0.875rem", padding: "5px 10px" }, // Ensures consistent height
                                       },
                                    }}
                                    sx={{ height: "32px", minHeight: "32px", "& .MuiInputBase-root": { height: "32px" } }}
                                    disabled={!isFormEditable}
                                 />
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={8}>
                              <FormControl sx={{ flexGrow: 1 }} error={!!errors.address}>
                                 <FormLabel>Address</FormLabel>
                                 <Input
                                    size="sm"
                                    placeholder="e.g., 1,000"
                                    name="address"
                                    value={donorData.address}
                                    onChange={handleInputChange}
                                    readOnly={!isFormEditable}
                                 />
                                 {errors.address && <FormHelperText>{errors.address}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl>
                                 <FormLabel>State</FormLabel>
                                 <Select
                                    size="sm"
                                    placeholder="Select"
                                    value={donorData.state}
                                    onChange={(event, newValue) => handleSelectChange(event, newValue, "state")}
                                    disabled={!isFormEditable}
                                 >
                                    {states.map((state) => (
                                       <Option key={state} value={state}>{state}</Option>
                                    ))}
                                 </Select>
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl error={!!errors.city}>
                                 <FormLabel>City</FormLabel>
                                 <Autocomplete
                                    size="sm"
                                    freeSolo
                                    placeholder="City"
                                    value={donorData.city}
                                    options={
                                       Array.from(new Set(
                                          donorData.state && cities[donorData.state as keyof typeof cities]
                                             ? cities[donorData.state as keyof typeof cities]
                                             : Object.values(cities).flat()
                                       ))
                                    }
                                    onInputChange={(_: React.SyntheticEvent, newInputValue: string) => {
                                       setDonorData((prev) => ({ ...prev, city: newInputValue }));
                                       if (newInputValue.length > 20 || !/^[A-Z ]*$/.test(newInputValue.toUpperCase())) {
                                          setErrors((prev) => ({ ...prev, city: "Max 20 characters. Only uppercase letters and spaces allowed." }));
                                       } else {
                                          setErrors((prev) => ({ ...prev, city: "" }));
                                       }
                                    }}
                                    getOptionLabel={(option: string) => typeof option === "string" ? option : ""}
                                    disabled={!isFormEditable}
                                 />
                                 {errors.city && <FormHelperText>{errors.city}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl error={!!errors.pincode}>
                                 <FormLabel>Pincode</FormLabel>
                                 <Input
                                    size="sm"
                                    placeholder="e.g., 000000"
                                    name="pincode"
                                    value={donorData.pincode}
                                    onChange={handleInputChange}
                                    readOnly={!isFormEditable}
                                 />
                                 {errors.pincode && <FormHelperText>{errors.pincode}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl error={!!errors.pan}>
                                 <FormLabel>PAN</FormLabel>
                                 <Input
                                    size="sm"
                                    placeholder="ABCDE1234F"
                                    name="pan"
                                    value={donorData.pan}
                                    onChange={handleInputChange}
                                    readOnly={!isFormEditable}
                                 />
                                 {errors.pan && <FormHelperText>{errors.pan}</FormHelperText>}
                              </FormControl>
                           </Grid>
                           <Grid xs={12} sm={4}>
                              <FormControl sx={{ flexGrow: 1 }}>
                                 <FormLabel>Type</FormLabel>
                                 <Select
                                    size="sm"
                                    placeholder="Select"
                                    value={donorData.donorType}
                                    onChange={(event, newValue) => handleSelectChange(event, newValue, "donorType")}
                                    disabled={!isFormEditable}
                                 >
                                    <Option value="Individual">Individual</Option>
                                    <Option value="Corporate">Corporate</Option>
                                    <Option value="Non Profit">Non Profit</Option>
                                    <Option value="Other">Other</Option>
                                 </Select>
                              </FormControl>
                           </Grid>
                        </Grid>
                     ) : (
                        <Typography level="body-sm" sx={{ mt: 2, textAlign: "center" }}>
                           Can't find any donor with this ID.
                        </Typography>
                     )}
                     {message && <Typography level="body-sm" sx={{ color: "green", mt: 1 }}>{message}</Typography>}
                     {error && <Typography level="body-sm" sx={{ color: "red", mt: 1 }}>{error}</Typography>}
                     <CardOverflow sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                        <Stack
                           direction="row"
                           spacing={2}
                           sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              my: 2,
                           }}
                        >
                           <Box sx={{ mt: 2, px: 1 }}>
                              <Typography level="body-xs" color="neutral">
                                 Created by: <strong>{donorData.createdBy || "N/A"}</strong> on {donorData.createdAt ? dayjs(donorData.createdAt).format("D MMM YYYY, h:mm A") : "N/A"}
                              </Typography>
                              <Typography level="body-xs" color="neutral">
                                 Last updated by: <strong>{donorData.updatedBy || "N/A"}</strong> on {donorData.updatedAt ? dayjs(donorData.updatedAt).format("D MMM YYYY, h:mm A") : "N/A"}
                              </Typography>
                           </Box>
                           {(showDonorForm || donorData.id) && (
                              <CardActions sx={{ alignSelf: "flex-end", pt: 0 }}>
                                 {isFormEditable && (
                                    <Button
                                       size="sm"
                                       variant="outlined"
                                       color="neutral"
                                       onClick={() => {
                                          if (!originalDonorData.id) {
                                             setShowDonorForm(false); // Show "Add New" button again
                                             setDonorData({
                                                id: "",
                                                name: "",
                                                phone: "",
                                                address: "",
                                                city: "",
                                                state: "",
                                                country: "",
                                                pincode: "",
                                                pan: "",
                                                email: "",
                                                birthdate: "",
                                                donorType: "",
                                                createdBy: "",
                                                updatedBy: "",
                                                createdAt: "",
                                                updatedAt: "",
                                             });
                                          } else {
                                             setDonorData(originalDonorData);
                                             setIsEditing(false);
                                             setIsFormEditable(false);
                                          }
                                       }}
                                    >
                                       Cancel
                                    </Button>
                                 )}
                                 {donorData.id ? (
                                    isEditing ? (
                                       <Button size="sm" variant="solid" onClick={handleUpdateDonor} disabled={isSubmitting || hasErrors}>
                                          {isSubmitting ? "Updating..." : "Update"}
                                       </Button>
                                    ) : (
                                       <Button size="sm" variant="outlined" onClick={() => {
                                          setOriginalDonorData(donorData); // Store the current donor data before editing
                                          setIsEditing(true);
                                          setIsFormEditable(true);
                                       }}>
                                          Edit
                                       </Button>
                                    )
                                 ) : (
                                    null
                                 )}
                              </CardActions>
                           )}
                        </Stack>
                     </CardOverflow>
                  </Card>
               </Stack>
            </Box>
         </Box>
      </Box>
   );
}

